import { Controller } from '@hotwired/stimulus'
import type { AlertType } from '../../components/Alert/types'
import type { CorporateFleetFormResponse } from './types'

const UNEXPECTED_ERROR_MESSAGE = 'Произошла ошибка при отправке заявки. Попробуйте позже'

export default class extends Controller {
    static targets = ['submitButton']

    static values = {
        submitUrl: String,
    }

    declare readonly submitButtonTarget: HTMLButtonElement
    declare readonly hasSubmitButtonTarget: boolean
    declare readonly submitUrlValue: string

    private submitting = false

    async submit(event: Event) {
        event.preventDefault()

        if (this.submitting) {
            return
        }

        this.clearFieldErrors()
        this.setSubmitting(true)

        try {
            const response = await fetch(this.submitUrlValue, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: new FormData(this.element as HTMLFormElement),
            })

            const data = await response.json() as CorporateFleetFormResponse

            if (response.ok && data.success) {
                this.reset()
                this.showAlert(data.message || 'Заявка успешно отправлена. Мы свяжемся с вами в ближайшее время.', 'success', 5000)

                return
            }

            if (data.errors) {
                const unmappedErrors = this.applyFieldErrors(data.errors)

                if (unmappedErrors.length > 0) {
                    this.showAlert(unmappedErrors.join('<br>'), 'error', 10000)
                }

            } else {
                this.showAlert(UNEXPECTED_ERROR_MESSAGE, 'error', 10000);
            }
        } catch {
            this.showAlert(UNEXPECTED_ERROR_MESSAGE, 'error', 10000)
        } finally {
            this.setSubmitting(false)
        }
    }

    private applyFieldErrors(errors: Record<string, string>): string[] {
        const unmappedErrors: string[] = []

        for (const [fieldName, message] of Object.entries(errors)) {
            const field = this.findFieldElement(fieldName)

            if (!field) {
                unmappedErrors.push(message)
                continue
            }

            if (field instanceof HTMLInputElement && field.type === 'checkbox') {
                field.closest('[data-controller~="checkbox"]')?.dispatchEvent(new CustomEvent('checkbox:add-error', { detail: { message } }))
                continue
            }

            if (field instanceof HTMLInputElement && field.type === 'file') {
                field.closest('[data-controller~="file-input"]')?.dispatchEvent(new CustomEvent('file-input:add-error', { detail: { message } }))
                continue
            }

            if (field instanceof HTMLTextAreaElement) {
                field.closest('[data-controller~="textarea"]')?.dispatchEvent(new CustomEvent('textarea:add-error', { detail: { message } }))
                continue
            }

            field.dispatchEvent(new CustomEvent('input:add-error', { detail: { message }, bubbles: true }))
        }

        return unmappedErrors
    }

    private findFieldElement(fieldName: string): HTMLElement | null {
        const form = this.element as HTMLFormElement

        return form.querySelector(`[name$="[${fieldName}]"]`) as HTMLElement | null
    }

    private clearFieldErrors() {
        const form = this.element as HTMLFormElement

        form.querySelectorAll('[data-controller~="input"] input').forEach((input) => {
            input.dispatchEvent(new CustomEvent('input:clear-error', { bubbles: true }))
        })

        form.querySelectorAll('[data-controller~="textarea"]').forEach((wrapper) => {
            wrapper.dispatchEvent(new CustomEvent('textarea:clear-error'))
        })

        form.querySelectorAll('[data-controller~="checkbox"]').forEach((wrapper) => {
            wrapper.dispatchEvent(new CustomEvent('checkbox:clear-error'))
        })

        form.querySelectorAll('[data-controller~="file-input"]').forEach((wrapper) => {
            wrapper.dispatchEvent(new CustomEvent('file-input:clear-error'))
        })
    }

    private reset() {
        const form = this.element as HTMLFormElement

        form.reset()
        this.clearFieldErrors()

        form.querySelectorAll('[data-controller~="file-input"]').forEach((wrapper) => {
            wrapper.dispatchEvent(new CustomEvent('file-input:reset'))
        })
    }

    private setSubmitting(submitting: boolean) {
        this.submitting = submitting

        if (!this.hasSubmitButtonTarget) {
            return
        }

        this.submitButtonTarget.disabled = submitting
        this.submitButtonTarget.classList.toggle('loading', submitting)
    }

    private showAlert(message: string, type: AlertType, timeout?: number) {
        window.dispatchEvent(new CustomEvent('alert:show', { detail: { message, type, timeout }, bubbles: true }))
    }
}
